import { apiGet, operationsClient } from '@/lib/api';
import { opsEstimateEndpoints } from '@/lib/api/endpoints';
import { estimateKeys } from '@/lib/queryKeys';
import { useApiQuery } from '@/hooks/useApiQuery';
import type { EstimateDocLayout } from '@/types/estimateDocTemplate';

/**
 * The block layout an authenticated estimate's PDF renders through, resolved by
 * the SERVER (`GET /estimates/:id/document-layout`) — the same contract the
 * customer-facing document uses (per-tenant gate → snapshot → chosen template →
 * tenant default → stock). The office preview/download read this instead of
 * re-resolving from the raw template row, so they never diverge from the
 * document the customer receives (e.g. when the estimate's pointer is stale or a
 * draft, the server falls back to the tenant default and this reflects it).
 *
 * Returns `null` when the estimate carries no template (classic PDF).
 *
 * `overrideDocTemplateId` previews an unsaved design pick in the estimate form:
 * `undefined` resolves the estimate as saved (detail/download), `null` resolves
 * the tenant default, and a template id resolves that specific design.
 */
export function useEstimateResolvedDocLayout(
  estimateId: string,
  enabled = true,
  overrideDocTemplateId?: string | null,
) {
  const previewDesign =
    overrideDocTemplateId === undefined ? undefined : (overrideDocTemplateId ?? 'default');
  return useApiQuery<{ layout: EstimateDocLayout | null }>({
    queryKey: estimateKeys.documentLayout(estimateId, previewDesign),
    queryFn: () =>
      apiGet<{ layout: EstimateDocLayout | null }>(
        operationsClient,
        opsEstimateEndpoints.documentLayout(estimateId, previewDesign),
      ),
    enabled: enabled && Boolean(estimateId),
    staleTime: 60_000,
    retry: false,
    showErrorToast: false,
  });
}
